import { useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";
import { dispatch } from "../../state/store";
import { setSelectedDashboardId } from "../../state/ducks";

import { alignItems, smartDashboardSections, strCode } from "../../consts";
import { withSection } from "../../hoc";
import { appUserService, t } from "../../services";
import { useFetchAvailableDashboards, useGenerateEmbedUrl } from "../../apiHooks";

import {
  DropdownInput,
  FlexContainers,
  Heading5,
  PageContainer,
  PageSelector,
  WrapperContainer
} from "../../components";
import { SmartDashboardContainer } from "../../containers";

import { DashboardHeader } from "./styled";

const smartDashboardPages = [
  { id: smartDashboardSections.PRESET, title: strCode.PRESET_TITLE },
  { id: smartDashboardSections.CUSTOM, title: strCode.CUSTOM_TITLE }
];

const SmartDashboardsPage = () => {
  const [activePage, setActivePage] = useState(smartDashboardSections.PRESET);

  const isAdmin = appUserService.isAdmin();

  const { loading: dashboardsLoading, fetchAvailableDashboards } = useFetchAvailableDashboards();
  const { loading: embedUrlLoading, generateEmbedUrl } = useGenerateEmbedUrl();

  const availableDashboards = useSelector((state) => state.dashboard.availableDashboards ?? []);
  const selectedDashboard = useSelector((state) => state.dashboard.selectedDashboardId);
  const dashboardEmbedUrl = useSelector((state) => state.dashboard.dashboardEmbedUrl);

  const dashboardOptions = useMemo(
    () =>
      availableDashboards.map((dashboard) => ({
        id: dashboard.dashboardId,
        value: dashboard.name
      })),
    [availableDashboards]
  );

  const isQnAUrl = useMemo(() => {
    return activePage === smartDashboardSections.CUSTOM;
  }, [selectedDashboard, activePage]);

  useEffect(() => {
    fetchAvailableDashboards();
  }, []);

  useEffect(() => {
    if (
      availableDashboards.length > 0 &&
      (!selectedDashboard || !availableDashboards.some((x) => x.dashboardId === selectedDashboard))
    ) {
      dispatch(setSelectedDashboardId(availableDashboards[0]?.dashboardId));
    }
  }, [availableDashboards, selectedDashboard]);

  useEffect(() => {
    if (selectedDashboard) {
      generateEmbedUrl(selectedDashboard, isQnAUrl);
    }
  }, [selectedDashboard, isQnAUrl]);

  return (
    <PageContainer alignItems={alignItems.start}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width="100%"
        height="100%">
        <WrapperContainer Container={FlexContainers.FlexRowStart} alignItems={"center"} gap={8}>
          <Heading5>{t(strCode.DASHBOARD_TITLE)}</Heading5>
        </WrapperContainer>

        <DashboardHeader Container={FlexContainers.FlexRowBetween} mt={8} gap={16}>
          {isAdmin && (
            <PageSelector
              pages={smartDashboardPages}
              activePage={activePage}
              setActivePage={setActivePage}
            />
          )}

          {activePage === smartDashboardSections.PRESET && (
            <DropdownInput
              id="dashboard-dropdown"
              options={dashboardOptions}
              selectedOption={
                selectedDashboard
                  ? Array.from(dashboardOptions).find((option) => option.id === selectedDashboard)
                  : null
              }
              onSelectOption={(id) => dispatch(setSelectedDashboardId(id))}
              disabled={dashboardsLoading || embedUrlLoading}
              hasError={!selectedDashboard}
              isSmall
            />
          )}
        </DashboardHeader>

        <SmartDashboardContainer dashboardUrl={dashboardEmbedUrl} isQnAUrl={isQnAUrl} />
      </WrapperContainer>
    </PageContainer>
  );
};

export default withSection(SmartDashboardsPage);
